import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Hammer, GraduationCap, Users, Lightbulb } from 'lucide-react';

const ServiceModelsSection = () => {
  const models = [
    {
      icon: <Hammer className="h-12 w-12 text-blue-600" />,
      title: "\"We Build It\"",
      subtitle: "Wir setzen Business Central für euch um",
      description: "Ihr braucht eine fertige Business Central Lösung, ohne euch um technische Details kümmern zu müssen? Wir übernehmen die gesamte Umsetzung - von der Konfiguration bis zur Schulung eures Teams.",
      features: [
        "Komplette Business Central Implementierung",
        "Datenübernahme aus bestehenden Systemen",
        "Anpassung an eure Geschäftsprozesse",
        "Umfassende Mitarbeiterschulungen",
        "Go-Live Begleitung und Support"
      ],
      idealFor: "Ideal für Unternehmen, die schnell eine funktionierende ERP-Lösung brauchen, aber selbst keine ERP-Erfahrung oder internen Ressourcen haben.",
      highlight: "Schlüsselfertige Lösung mit Rundum-Service"
    },
    {
      icon: <GraduationCap className="h-12 w-12 text-green-600" />,
      title: "\"We Empower You\"",
      subtitle: "Wir bilden euer Team aus und begleiten euch",
      description: "Euer Team möchte Business Central verstehen und langfristig selbst verwalten? Wir vermitteln Wissen, bilden interne Ansprechpartner aus und stehen als Sparringspartner zur Seite.",
      features: [
        "Intensive Schulungen für euer Team",
        "Ausbildung interner Business Central Experten",
        "Best Practices und Prozess-Know-how",
        "Langfristige Beratung und Sparring",
        "Unabhängigkeit durch Wissensvermittlung"
      ],
      idealFor: "Ideal für Unternehmen, die intern Kompetenzen aufbauen, eigene Ansprechpartner haben und langfristig unabhängig arbeiten möchten.",
      highlight: "Nachhaltiger Wissenstransfer für eure Unabhängigkeit"
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Wie wir zusammenarbeiten
          </h2>
          <p className="text-lg text-gray-600">
            Wir passen uns flexibel an eure Bedürfnisse an - als vollständiger Umsetzer oder als 
            strategischer Partner, der euer Team befähigt und Wissen vermittelt.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {models.map((model, index) => (
            <Card key={index} className="border-2 border-gray-200 hover:border-blue-200 hover:shadow-xl transition-all duration-300 h-full">
              <CardContent className="p-8 flex flex-col h-full">
                {/* Icon und Titel */}
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    {model.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{model.title}</h3>
                  <p className="text-lg font-semibold text-blue-600">{model.subtitle}</p>
                </div>

                {/* Beschreibung */}
                <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                  {model.description}
                </p>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-blue-600" />
                    Das beinhaltet:
                  </h4>
                  <ul className="space-y-2">
                    {model.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Highlight */}
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <Lightbulb className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                    <p className="text-blue-800 font-semibold">
                      {model.highlight}
                    </p>
                  </div>
                </div>

                {/* Ideal für */}
                <div className="bg-green-50 rounded-lg p-4 mt-auto">
                  <p className="text-green-800 font-medium">
                    👉 {model.idealFor}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Zusätzliche Information */}
        <div className="mt-16 bg-white rounded-xl p-8 max-w-4xl mx-auto shadow-lg">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">
              Warum ist ein interner Ansprechpartner so wichtig?
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="bg-blue-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Schnelle Hilfe</h4>
              <p className="text-sm text-gray-600">
                Bei Fragen oder Problemen ist sofort jemand da, der das System kennt
              </p>
            </div>
            <div className="text-center p-4">
              <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <GraduationCap className="h-8 w-8 text-green-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Kontinuierliche Weiterentwicklung</h4>
              <p className="text-sm text-gray-600">
                Euer interner Experte kann das System stetig optimieren und anpassen
              </p>
            </div>
            <div className="text-center p-4">
              <div className="bg-purple-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Lightbulb className="h-8 w-8 text-purple-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Unabhängigkeit</h4>
              <p className="text-sm text-gray-600">
                Ihr seid nicht dauerhaft auf externe Dienstleister angewiesen
              </p>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-6">
              <strong>Unser Ziel:</strong> Dass ihr nach der Einführung selbstständig mit Business Central arbeiten könnt 
              und nur bei größeren Projekten oder strategischen Fragen auf uns zurückkommt.
            </p>
            <a 
              href="#contact" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Zusammenarbeitsmodell besprechen
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceModelsSection; 